import flatten from 'lodash/flatten';
import keyBy from 'lodash/keyBy';
import React, { ReactNode } from 'react';

import { CORRECTION_POPUP_ANCHOR_ID } from 'reducers/transcription';
import {
  Phrase,
  PhraseRange,
  TranscriptElementIndex,
} from 'utilities/transcript/types';

const fillerRegExp = new RegExp(/\b[Uu][mh]\b/, 'gi');

export const escapeSpecialChars = (str: string): string => {
  const specials = /[.*+?|()[\]{}\\$^]/g; // .*+?|()[]{}\$^

  return str.replace(specials, '\\$&');
};

export const searchPhraseRegExp = (searchPhrase: string): RegExp => {
  return new RegExp(`(${escapeSpecialChars(searchPhrase)})`, 'gi');
};

export const highlightFillerWords = (
  phrase: string,
  className: string
): (string | ReactNode)[] => {
  const matches = [...phrase.matchAll(fillerRegExp)];
  const result: (string | JSX.Element)[] = [];
  let remainingIndex = 0;

  for (const match of matches) {
    if (!match.index) {
      continue;
    }

    if (remainingIndex < match.index) {
      // processing first match... check if there's any text before the first match
      if (match.index > 0) {
        result.push(phrase.substring(remainingIndex, match.index));
      }
    }

    result.push(
      <mark key={match.index} className={className}>
        {match[0]}
      </mark>
    );
    remainingIndex = match.index + match[0].length;
  }

  result.push(phrase.substring(remainingIndex));

  return result;
};

// function that replaces all instances of the searched phrase in
// the phrase value with the searched phrase surrounded by mark tags
export const replaceSearchWordsInPhrase = (
  phraseValue: string,
  searchPhrase: string,
  baseClass = '',
  activeClass = '',
  activeIndex = 0
): (string | ReactNode)[] | undefined => {
  try {
    // note: because the Regexp has a capture group, the matched text
    // will be included as segments too.
    const phraseSegments = phraseValue.split(searchPhraseRegExp(searchPhrase));

    if (phraseSegments.length === 1) {
      // search term not found
      return [phraseValue];
    }

    const result: (string | JSX.Element)[] = [];
    const lowerSearch = searchPhrase.toLowerCase();
    let index = 0;

    for (const segment of phraseSegments) {
      if (segment !== '') {
        if (segment.toLowerCase() !== lowerSearch) {
          // this is not a search occurrence/match
          result.push(segment);
        } else {
          // this segment was a match
          let currentClass = baseClass;

          if (activeIndex === index) {
            currentClass += ` ${activeClass}`;
          }

          result.push(
            <mark key={index} className={currentClass} data-index={index}>
              {segment}
            </mark>
          );
          index++;
        }
      }
    }

    return result;
  } catch (err) {
    console.warn('Error replacing search words in phrase', err);

    if (err) {
      return [phraseValue];
    }
  }
};

// function that returns all indices of of the searchPhrase
// in phrases transcript array
export const findIndicesOfSearchPhraseInPhrases = (
  searchPhrase: string,
  phrases: Phrase[]
): Array<{ index: number; counter: number }> => {
  searchPhrase = searchPhrase.trim();

  if (!searchPhrase || phrases.length < 1) {
    return [];
  }

  const result: { index: number; counter: number }[] = [];

  try {
    const regex = searchPhraseRegExp(searchPhrase);

    phrases.forEach((phraseObj, index) => {
      const phrase = phraseObj.value;
      let counter = 0;

      while (regex.exec(phrase)) {
        result.push({ index, counter });
        counter++;
      }
    });

    return result;
  } catch (err) {
    console.warn('Error finding indices of Search Phrase In Phrases', err);

    return [];
  }
};

type Segment = {
  value: string;
  selectable?: boolean;
  sourceTag?: string;
};

export const rangeToTag = (range: PhraseRange): string => {
  return `${range.source.monologue}-${range.source.element}`;
};

export const getWordSegments = (
  phraseValue: string,
  ranges: PhraseRange[]
): Segment[] => {
  // we only care about the 'text' ranges here
  const textRanges = ranges.filter(range => range.type === 'text');
  const output: Segment[] = [];
  let cursor = 0;

  if (!textRanges.length) {
    return [{ value: phraseValue }];
  }

  // account for characters at start not part of range
  if (textRanges[0].start > 0) {
    output.push({
      value: phraseValue.substring(0, textRanges[0].start),
    });
  }

  textRanges.forEach(range => {
    if (cursor && cursor < range.start) {
      // account for empty space between end of last range, and beginning of current
      output.push({
        value: phraseValue.substring(cursor, range.start),
      });
    }

    const endIndex = range.start + range.length;

    output.push({
      value: phraseValue.substring(range.start, endIndex),
      sourceTag: rangeToTag(range),
      selectable: true,
    });
    cursor = endIndex;
  });

  // account for any text at the end of the value string not accounted for by ranges
  if (cursor < phraseValue.length) {
    output.push({
      value: phraseValue.substring(cursor, phraseValue.length),
    });
  }

  return output;
};

export const makeWordsDetectable = (
  phraseValue: string,
  wordClassName: string,
  searchPhrase = '',
  searchMatchBaseClass = '',
  searchMatchActiveClass = '',
  // TODO: SFP - not using this parameter yet so none of the search hits
  //      will be identified as the active one. Should be addressed in CREAT-1041.
  _activeSearchMatchIndex = -1,
  ranges: PhraseRange[],
  selectedElementSource?: string
): (string | ReactNode)[] => {
  const segments = getWordSegments(phraseValue, ranges);

  if (segments.length === 1) {
    // words not found
    return [phraseValue];
  }

  let format = (segment: string) => segment as string | ReactNode;

  if (searchPhrase.length) {
    format = (segment: string) => {
      const activeIndex = -1; // TODO: CREAT-1041 - compute the activeIndex to make the correct search it be marked as active

      return replaceSearchWordsInPhrase(
        segment,
        searchPhrase,
        searchMatchBaseClass,
        searchMatchActiveClass,
        activeIndex
      );
    };
  }

  const result: (string | ReactNode)[] = [];
  let charCount = 0;

  for (const [key, segment] of segments.entries()) {
    if (segment.value !== '') {
      if (!segment.selectable) {
        // this is not a word match, maybe whitespace or punctuation, etc
        result.push(segment.value);
      } else {
        // this segment is a single word

        const isPopupAnchor = segment.sourceTag === selectedElementSource;

        result.push(
          <span
            key={key}
            className={wordClassName}
            data-start-index={charCount}
            data-is-word={true}
            data-source-tag={segment.sourceTag}
            id={isPopupAnchor ? CORRECTION_POPUP_ANCHOR_ID : undefined}
          >
            {format(segment.value)}
          </span>
        );
      }
    }

    charCount += segment.value.length;
  }

  return result;
};

/**
 * Finds the source element index information for each occurrence of a word in the transcript source.
 * @param {Phrase[]} phrases - transcript phrases to inspect
 * @param {string } word - the words we're looking for
 * @return {TranscriptElementIndex[]} - list of the source indexes for each occurrence of the word
 *   in the given transcript phrases.
 */
export const findTranscriptSourceIndexes = (
  phrases: Phrase[],
  word: string
): TranscriptElementIndex[] => {
  // TODO: SFP - unit tests

  const wordRegExp = new RegExp(`\\b${escapeSpecialChars(word)}\\b`, 'gi');
  // initialize array of arrays with the final size beforehand
  const indexesPerPhrase = new Array<TranscriptElementIndex[]>(phrases.length);

  for (const [phraseIndex, phrase] of phrases.entries()) {
    const matches = [...phrase.value.matchAll(wordRegExp)];
    // initialize array with the final size beforehand
    const phraseSources = (indexesPerPhrase[phraseIndex] =
      new Array<TranscriptElementIndex>(matches.length));

    if (matches.length > 0) {
      const rangesByStartIndex = keyBy(phrase.ranges, 'start');

      for (const [index, match] of matches.entries()) {
        const startIndex = match.index;

        if (startIndex != null) {
          phraseSources[index] =
            rangesByStartIndex[startIndex.toString()].source;
        }
      }
    }
  }

  return flatten(indexesPerPhrase);
};

/**
 * returns the occurrence index for the word appearing at a given position in a piece of text
 * @param {string} text - the text to look for matches of the word
 * @param {string} word - the word we're looking for
 * @param (number} occurrenceCharIndex - the expected character index of the word in the text
 * @return {number}
 */
export const findIndexOfOccurrence = (
  text: string,
  word: string,
  occurrenceCharIndex: number
): number | null => {
  // TODO: SFP - unit tests
  const wordRegExp = new RegExp(`\\b${escapeSpecialChars(word)}\\b`, 'gi');
  const matches = [...text.matchAll(wordRegExp)];

  for (const [index, match] of matches.entries()) {
    if (match.index === occurrenceCharIndex) {
      return index;
    }
  }

  return null;
};
